import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Lock, X, Shield, Star, Package, Building, Search } from "lucide-react";
import axios from "axios";
import { useMediaQuery } from "react-responsive";
import lightlogo from "../assets/lightmodelogo.png";
import darklogo from "../assets/darkmodelogo.png";
import favicon from "../assets/favicon_new.png";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import CreateTemplate from "./CreateTemplate";
import BillingSection from "./BillingSection";
import HistorySection from "./HistorySection";
import ScanSection from "./ScanSection";
import SupportPage from "./SupportPage";
import EditTemplate from "./EditTemplate";
import { supabase } from "../supabaseClient";
import confetti from "canvas-confetti";
import IntegrationsPage from "./IntegrationsPage";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import useFeatures from "../hooks/useFeatures";
import CompanyPage from "./CompanyPage";
import UsageSection from "./UsageSection";
import TemplateSection from "./TemplateSection";
import StatusSection from "./StatusSection";
import ReviewSection from "./ReviewSection";
import DashboardHeader from "./DashboardHeader";
import DashboardSidebar from "./DashboardSidebar";
import DashboardLayout from "./DashboardLayout";
import Notification from "./Notification";
import { useLocation } from "react-router-dom";

ChartJS.register(ArcElement, Tooltip, Legend);

axios.defaults.baseURL =
  process.env.REACT_APP_API_BASE_URL || "https://api.clevrscan.com"; // Backend URL

export default function Dashboard() {
  const isMobile = useMediaQuery({ query: "(max-width: 900px)" });
  const location = useLocation();

  // Helper function to get valid section from URL
  const getInitialSection = () => {
    const params = new URLSearchParams(location.search);
    const section = params.get("section");

    const validSections = [
      "scan",
      "history",
      "status",
      "templates",
      "usage",
      "billing",
      "support",
      "integrations",
      "companies",
      "review",
    ];

    return validSections.includes(section) ? section : "scan";
  };

  // Initialize with the correct section from URL
  const [activeSection, setActiveSection] = useState(getInitialSection);

  const [capturedImages, setCapturedImages] = useState([]); // [{ image: '', filename: '' }, ...]
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true); // New state for initial loading
  const videoRef = useRef(null);
  const navigate = useNavigate();
  const [taskIds, setTaskIds] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [expandedTasks, setExpandedTasks] = useState({});
  const [documentHistory, setDocumentHistory] = useState([]);
  const [expandedDocs, setExpandedDocs] = useState({});
  const hasInProgressTasks = useMemo(
    () =>
      statuses.some(
        (s) => s.state && !["SUCCESS", "FAILURE"].includes(s.state)
      ),
    [statuses]
  );
  const [batchProcess, setBatchProcess] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [_totalPages, setTotalPages] = useState(1);
  const [totalDocuments, setTotalDocuments] = useState(0);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [paginationInfo, setPaginationInfo] = useState(null);
  const [splitPdf, setSplitPdf] = useState(
    localStorage.getItem("username") === "Fundly Test" ||
      localStorage.getItem("username") === "Vivek" ||
      localStorage.getItem("username") === "Analytics"
  );
  const [isDarkMode, setIsDarkMode] = useState(true);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false); // New state for mobile menu
  const [_usageStats, setUsageStats] = useState(null);
  const [isHovering, setIsHovering] = useState(false);
  const [_statsLoading, setStatsLoading] = useState(true);
  const [_statsError, setStatsError] = useState(null);
  const [_showSupportForm, _setShowSupportForm] = useState(false);
  const [_supportMessage, _setSupportMessage] = useState("");
  const [_supportCategory, _setSupportCategory] = useState("general");
  const [_sendingSupport, _setSendingSupport] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templatesError, setTemplatesError] = useState(null);
  const [showCreateTemplate, setShowCreateTemplate] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null); // Stores full template object, not just ID
  const [showEditTemplate, setShowEditTemplate] = useState(false);
  const [templateNameCache, setTemplateNameCache] = useState({});
  const [templateNames, setTemplateNames] = useState({});
  const [resultViewMode, setResultViewMode] = useState({}); // { [itemId]: 'formatted' | 'json' }
  const [showAccountDropdown, setShowAccountDropdown] = useState(false);
  const [username, _setUsername] = useState(
    localStorage.getItem("username") || "User"
  );
  const [_email, _setEmail] = useState(localStorage.getItem("email") || "");
  const [_isFundlyUser, _setIsFundlyUser] = useState(
    localStorage.getItem("username") === "Fundly Test" ||
      localStorage.getItem("username") === "Vivek" ||
      localStorage.getItem("username") === "Analytics"
  );
  const accountDropdownRef = useRef(null);
  const [_currentPlan, _setCurrentPlan] = useState("free");
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [_nextBillingDate, _setNextBillingDate] = useState("2024-04-15");
  const [_paymentMethod, _setPaymentMethod] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [_showPaymentSuccessModal, setShowPaymentSuccessModal] =
    useState(false);
  const [_showPaymentFailedModal, setShowPaymentFailedModal] = useState(false);
  const [_showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [_upgradeMessage, _setUpgradeMessage] = useState("");
  const [_userSubscription, _setUserSubscription] = useState(null);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [_showSuccessNotification, setShowSuccessNotification] =
    useState(false);
  const [apiKeys, setApiKeys] = useState([]);
  const [_showCreateKeyModal, setShowCreateKeyModal] = useState(false);
  const [newKeyName, setNewKeyName] = useState("");
  const [_keyCreationLoading, setKeyCreationLoading] = useState(false);
  const [_showSheetSelector, setShowSheetSelector] = useState(false);
  const [excelSheets, setExcelSheets] = useState([]);
  const [currentExcelFile, setCurrentExcelFile] = useState(null);
  const [selectedSheets, setSelectedSheets] = useState([]); // Track selected sheets for multi-selection
  const [_activeIntegration, setActiveIntegration] = useState(null);
  const [_showSupport, _setShowSupport] = useState(false);

  // Tally ledger options state
  const [withInventoryLedger, setWithInventoryLedger] = useState(false);
  const [withoutInventoryLedger, setWithoutInventoryLedger] = useState(true); // Default to without inventory ledger

  // Multiple files confirmation dialog states
  const [_showMultipleFilesDialog, setShowMultipleFilesDialog] =
    useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);

  // Feature flags and company management
  const {
    hasFeature,
    hasCompanyManagement,
    _hasTallyIntegration,
    hasIntegrationsFeature,
    getApiHeaders,
    getUploadEndpoint,
    getStatusEndpoint,
    _apiConfig,
    features,
    loading: _featuresLoading,
    error: _featuresError,
  } = useFeatures();
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);

  // Search functionality for document history - simplified for isolated component
  const [searchQuery, setSearchQuery] = useState("");
  const [historyStartDate, setHistoryStartDate] = useState("");
  const [historyEndDate, setHistoryEndDate] = useState("");
  const [historyStatusFilter, setHistoryStatusFilter] = useState("all");
  const [historyStatusCounts, setHistoryStatusCounts] = useState(null);
  const [isExportingHistoryExcel, setIsExportingHistoryExcel] = useState(false);

  // View mode state for document history (per document instead of global)
  const [documentViewModes, setDocumentViewModes] = useState({}); // Track view mode per document ID
  const [presignedUrls, setPresignedUrls] = useState({}); // Cache for presigned URLs
  const [loadingUrls, setLoadingUrls] = useState({}); // Track loading state for URLs
  const [reprocessingDocuments, setReprocessingDocuments] = useState({}); // Track reprocessing state
  const [hasUnviewedCompletedTasks, setHasUnviewedCompletedTasks] =
    useState(false); // Visual indicator for completed tasks
  const [lastCompletionStatus, setLastCompletionStatus] = useState(null); // Track if last completion was success/failure
  const [editingDocuments, setEditingDocuments] = useState({}); // Track which documents are in edit mode
  const [savingDocuments, setSavingDocuments] = useState({}); // Track saving state
  const [showStatusDot, setShowStatusDot] = useState(false); // Yellow dot indicator for status icon

  // Review section state
  const [reviewDocuments, setReviewDocuments] = useState([]); // List of documents for review
  const [expandedReviewDocs, setExpandedReviewDocs] = useState({}); // Track expanded review items
  const [isLoadingReview, setIsLoadingReview] = useState(false); // Loading state for review
  const [showReviewedDocs, setShowReviewedDocs] = useState(false); // Toggle between reviewed/non-reviewed
  const [markingAsVerified, setMarkingAsVerified] = useState({}); // Track which documents are being marked as verified
  const [hasPendingReviewDocs, setHasPendingReviewDocs] = useState(false); // Red dot for review icon when any document needs verification

  // Simple search handlers for the isolated component
  const searchTimeoutRef = useRef(null);
  const historyStartDateRef = useRef("");
  const historyEndDateRef = useRef("");
  const historyStatusFilterRef = useRef("all");

  useEffect(() => {
    historyStartDateRef.current = historyStartDate;
  }, [historyStartDate]);

  useEffect(() => {
    historyEndDateRef.current = historyEndDate;
  }, [historyEndDate]);

  useEffect(() => {
    historyStatusFilterRef.current = historyStatusFilter;
  }, [historyStatusFilter]);

  const _handleSearchChange = useCallback((value) => {
    setSearchQuery(value);
    // Debounce the search to avoid too many API calls
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      fetchDocumentHistory(1, value, true);
    }, 300);
  }, []);

  const _clearSearch = useCallback(() => {
    setSearchQuery("");
    // Clear any pending search timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    // Refresh the first page without search
    fetchDocumentHistory(1, "", true);
  }, []);

  // Fetch review documents from API
  const fetchReviewDocuments = useCallback(async (flag = null) => {
    try {
      setIsLoadingReview(true);
      const token = localStorage.getItem("authToken");
      if (!token) {
        console.error("No auth token found");
        return;
      }

      const url = flag
        ? `/api/review_documents?flag=${encodeURIComponent(flag)}`
        : "/api/review_documents";

      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      console.log("Review documents API response:", response.data);
      console.log(
        "Number of documents received:",
        response.data.documents?.length || 0
      );
      response.data.documents?.forEach((doc) => {
        console.log(
          `Document ${doc.id}: ${doc.name}, low_confidence_count: ${doc.low_confidence_count}, flag: ${doc.flag}`
        );
      });

      const docs = response.data.documents || [];
      setReviewDocuments(docs);
      // Update pending flag: any document that needs verification
      if (flag === "needs verification" || !flag) {
        const needsVerification = docs.some(
          (d) =>
            d.flag === "needs verification" ||
            d.needs_verification === true ||
            d.isReviewed === false
        );
        setHasPendingReviewDocs(needsVerification);
      } else {
        // If viewing a filtered list that isn't "needs verification", query the pending list to keep indicator accurate
        try {
          const pendingResp = await axios.get(
            "/api/review_documents?flag=needs%20verification",
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            }
          );
          const pendingDocs = pendingResp.data.documents || [];
          setHasPendingReviewDocs(pendingDocs.length > 0);
        } catch (e) {
          console.error("Error checking pending review documents:", e);
        }
      }
    } catch (error) {
      console.error("Error fetching review documents:", error);
      setReviewDocuments([]);
      setHasPendingReviewDocs(false);
    } finally {
      setIsLoadingReview(false);
    }
  }, []);

  // Mark document as verified
  const markDocumentAsVerified = async (docId) => {
    try {
      setMarkingAsVerified((prev) => ({ ...prev, [docId]: true }));
      const token = localStorage.getItem("authToken");
      if (!token) {
        console.error("No auth token found");
        return;
      }

      const response = await axios.put(
        `/api/review_documents/${docId}/verify`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.success) {
        // Refresh the review documents list
        const currentFlag = showReviewedDocs
          ? "verified"
          : "needs verification";
        await fetchReviewDocuments(currentFlag);

        // Show success message
        alert("Document marked as verified!");
        // After verification, pending docs may decrease; pending flag will be updated by fetch
      }
    } catch (error) {
      console.error("Error marking document as verified:", error);
      alert("Failed to mark document as verified. Please try again.");
    } finally {
      setMarkingAsVerified((prev) => ({ ...prev, [docId]: false }));
    }
  };

  // Fetch review documents when toggle changes
  useEffect(() => {
    if (activeSection === "review") {
      const flag = showReviewedDocs ? "verified" : "needs verification";
      fetchReviewDocuments(flag);
    }
  }, [showReviewedDocs, activeSection, fetchReviewDocuments]);

  // Handle ledger option changes (mutually exclusive)
  const handleWithInventoryLedgerChange = (checked) => {
    if (checked) {
      setWithInventoryLedger(true);
      setWithoutInventoryLedger(false);
    } else {
      // Ensure at least one is always selected
      if (!withoutInventoryLedger) {
        setWithoutInventoryLedger(true);
      }
      setWithInventoryLedger(false);
    }
  };

  const handleWithoutInventoryLedgerChange = (checked) => {
    if (checked) {
      setWithoutInventoryLedger(true);
      setWithInventoryLedger(false);
    } else {
      // Ensure at least one is always selected
      if (!withInventoryLedger) {
        setWithInventoryLedger(true);
      }
      setWithoutInventoryLedger(false);
    }
  };

  // New handler for Need Help button
  const _handleNeedHelpClick = () => {
    setActiveSection("support");
  };

  // Update usage stats function
  const updateUsageStats = (newStats) => {
    setUsageStats(newStats);
  };

  // Since search is now handled by the backend, filteredDocuments is just the current page
  const filteredDocuments = useMemo(() => {
    return documentHistory;
  }, [documentHistory]);

  useEffect(() => {
    const token = localStorage.getItem("authToken");
    const isAuthenticated = localStorage.getItem("isAuthenticated");

    if (!token) {
      navigate("/");
      return;
    }

    // Clear the authentication flag after successful load
    if (isAuthenticated) {
      localStorage.removeItem("isAuthenticated");
    }
  }, [navigate]);

  useEffect(() => {
    if (activeSection === "scan" && videoRef.current) {
      startCamera();
    }
    return () => {
      if (videoRef.current && videoRef.current.srcObject) {
        const tracks = videoRef.current.srcObject.getTracks();
        tracks.forEach((track) => track.stop());
      }
    };
  }, [activeSection, videoRef.current]);

  // Handle URL changes (for navigation within the app)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const section = params.get("section");

    const validSections = [
      "scan",
      "history",
      "status",
      "templates",
      "usage",
      "billing",
      "support",
      "integrations",
      "companies",
      "review",
    ];

    if (section && validSections.includes(section)) {
      setActiveSection(section);
    } else if (!section) {
      setActiveSection("scan");
    }
  }, [location.search]);

  // Clear the "unviewed completed tasks" indicator as soon as user opens History
  useEffect(() => {
    if (activeSection === "history") {
      setHasUnviewedCompletedTasks(false);
      setLastCompletionStatus(null);
    }
  }, [activeSection]);

  useEffect(() => {
    if (taskIds.length === 0) return;

    // Only poll status API when in status or history sections
    if (activeSection !== "status" && activeSection !== "history") return;

    // Initial fetch
    fetchTaskStatuses();

    // Set up interval for periodic updates
    const intervalId = setInterval(fetchTaskStatuses, 2000);

    // Cleanup on unmount or when taskIds/activeSection changes
    return () => clearInterval(intervalId);
  }, [taskIds, activeSection]);

  const fetchTaskStatuses = useCallback(async () => {
    if (activeSection !== "status" && activeSection !== "history") return;
    if (taskIds.length === 0) return;

    try {
      const updatedStatuses = await Promise.all(
        taskIds.map(async (taskId) => {
          // Use feature system for API endpoint and headers
          const statusEndpoint = getStatusEndpoint(taskId);
          const headers = getApiHeaders();

          const response = await axios.get(statusEndpoint, {
            headers,
          });

          console.log(`Status response for task ${taskId}:`, response.data);

          // Update template names if we receive a valid template name
          if (response.data.template_id) {
            // Even if template_name is missing, we should still cache the ID
            setTemplateNames((prev) => ({
              ...prev,
              [response.data.template_id]:
                response.data.template_name ||
                templateNameCache[response.data.template_id] ||
                "Unknown Template",
            }));

            // Also update templateNameCache for consistency
            setTemplateNameCache((prev) => ({
              ...prev,
              [response.data.template_id]:
                response.data.template_name ||
                prev[response.data.template_id] ||
                "Unknown Template",
            }));
          }

          if (
            response.data.state === "SUCCESS" &&
            response.data.result &&
            response.data.result.usage_stats
          ) {
            updateUsageStats(response.data.result.usage_stats);
          }

          return response.data;
        })
      );

      // Only update state if we're in the status section
      setStatuses(updatedStatuses);

      // Remove completed tasks from taskIds and refresh history if needed
      const completedStatuses = updatedStatuses.filter((status) =>
        ["SUCCESS", "FAILURE"].includes(status.state)
      );
      const completedTaskIds = completedStatuses.map(
        (status) => status.task_id
      ); // Fixed: use task_id instead of taskId

      if (completedTaskIds.length > 0) {
        setTaskIds((prev) =>
          prev.filter((id) => !completedTaskIds.includes(id))
        );

        // Track the completion status for indicator color
        const hasSuccessfulTasks = completedStatuses.some(
          (status) => status.state === "SUCCESS"
        );
        const hasFailedTasks = completedStatuses.some(
          (status) => status.state === "FAILURE"
        );

        if (hasSuccessfulTasks && hasFailedTasks) {
          setLastCompletionStatus("mixed"); // Some success, some failure
        } else if (hasSuccessfulTasks) {
          setLastCompletionStatus("success"); // All successful
        } else {
          setLastCompletionStatus("failure"); // All failed
        }

        // If we're on the history section, refresh to show newly completed documents
        if (activeSection === "history") {
          // Use a timeout to avoid calling fetchDocumentHistory directly
          setTimeout(() => {
            // Trigger a refresh by updating a state that the history useEffect depends on
            setCurrentPage((prev) => prev);
          }, 1000); // Small delay to ensure backend is updated
          // Clear the unviewed indicator since user is already viewing history
          setHasUnviewedCompletedTasks(false);
          setLastCompletionStatus(null);
        } else {
          // If user is not in history section, show the indicator
          setHasUnviewedCompletedTasks(true);
        }

        // No longer using status updates indicator here (dot is for in-progress only)
      }
    } catch (error) {
      console.error(
        "Error fetching task statuses:",
        error.response?.data || error
      );
    }
  }, [
    activeSection,
    taskIds,
    getStatusEndpoint,
    getApiHeaders,
    templateNameCache,
    currentPage,
    searchQuery,
  ]);

  const startCamera = async () => {
    try {
      if (isMobile) {
        // Don't start the camera stream on mobile - we'll use input[type="file"] instead
        return;
      }
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      console.error("Error accessing the camera", err);
    }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    // Check if multiple files are uploaded - always show dialog for multiple files
    if (files.length > 1) {
      setPendingFiles(files);
      setShowMultipleFilesDialog(true);
      return;
    }

    // Process files normally
    processFiles(files);
  };

  const processFiles = async (files) => {
    for (const file of files) {
      // Check if it's an Excel file
      if (
        file.type ===
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
        file.type === "application/vnd.ms-excel"
      ) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: "array" });

          // Get all sheet names
          const sheetNames = workbook.SheetNames;
          setExcelSheets(sheetNames);
          setCurrentExcelFile(file);
          setShowSheetSelector(true);
        };
        reader.readAsArrayBuffer(file);
      } else {
        // Check if it's a CSV file
        const isCsvFile = file.type === "text/csv" || file.name.toLowerCase().endsWith(".csv");
        const isPdfFile = file.type === "application/pdf";
        
        // Handle normal files as before
        setCapturedImages((prev) => [
          ...prev,
          {
            file,
            displayImage: !isPdfFile && !isCsvFile ? URL.createObjectURL(file) : null,
            filename: file.name,
            isPdf: isPdfFile,
            isCsv: isCsvFile,
            isSpreadsheet: isCsvFile,
          },
        ]);
      }
    }
  };

  const _handleMultipleFilesConfirm = (treatAsSingle) => {
    if (treatAsSingle) {
      // Treat all files as single document - turn OFF batch processing
      setBatchProcess(false);
      processFiles(pendingFiles);
    } else {
      // Treat each file as separate document - turn ON batch processing
      setBatchProcess(true);
      processFiles(pendingFiles);
    }

    // Clean up
    setShowMultipleFilesDialog(false);
    setPendingFiles([]);
  };

  const _handleMultipleFilesCancel = () => {
    setShowMultipleFilesDialog(false);
    setPendingFiles([]);
  };

  const discardImage = (index) => {
    setCapturedImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleLogout = async () => {
    try {
      // Sign out from Supabase first
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error("Error signing out from Supabase:", error);
      }

      // Remove local storage items
      localStorage.removeItem("authToken");
      localStorage.removeItem("username");
      localStorage.removeItem("email");

      // Force a small delay to ensure Supabase state is cleared
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Redirect the user to the login page
      window.location.href = "/"; // Use window.location instead of navigate
    } catch (error) {
      console.error("Logout error:", error);
      // Fallback: force logout anyway
      localStorage.clear();
      window.location.href = "/";
    }
  };

  const submitImages = async () => {
    console.log("Submit triggered. Selected template:", selectedTemplate);
    console.log("Captured images:", capturedImages);

    if (!selectedTemplate) {
      alert("Please select a template before proceeding.");
      return;
    }

    if (capturedImages.length === 0) {
      alert("Please capture or upload at least one image before proceeding.");
      return;
    }

    // Check if company selection is required and validate
    if (hasCompanyManagement() && !selectedCompany) {
      alert("Please select a company before proceeding.");
      return;
    }

    // Validate ledger options for tally users
    if (
      hasFeature("tally_integration") &&
      !withInventoryLedger &&
      !withoutInventoryLedger
    ) {
      alert("Please select a ledger option before proceeding.");
      return;
    }

    const _token = localStorage.getItem("authToken");
    try {
      setLoading(true);
      const formData = new FormData();

      // Append files to form data
      capturedImages.forEach((item) => {
        if (item.file) {
          formData.append("files", item.file);
        }
      });

      // Find the selected template name and cache it immediately
      const selectedTemplateName = selectedTemplate.name;
      if (selectedTemplateName) {
        setTemplateNameCache((prev) => ({
          ...prev,
          [selectedTemplate.id]: selectedTemplateName,
        }));
      }

      formData.append("template", selectedTemplate.id);
      formData.append("batchProcess", batchProcess ? "true" : "false");
      formData.append("splitPdf", splitPdf ? "true" : "false");

      // Add ledger option for tally users
      if (hasFeature("tally_integration")) {
        formData.append("inventory", withInventoryLedger ? "true" : "false");
      }

      // Add company information if company management is enabled and a company is selected
      if (hasCompanyManagement() && selectedCompany) {
        formData.append("company_id", selectedCompany.id);
      }

      console.log("BATCH PROCESS", batchProcess);
      console.log("SPLIT PDF", splitPdf);

      // Use feature system for API endpoint and headers
      const uploadEndpoint = getUploadEndpoint();
      const headers = getApiHeaders(); // Remove Content-Type override for FormData

      const response = await axios.post(uploadEndpoint, formData, {
        headers,
      });

      console.log("Upload response:", response.data);

      // Make sure we have template_id and template_name cached
      if (response.data.template_id) {
        // Cache template name if available
        setTemplateNameCache((prev) => ({
          ...prev,
          [response.data.template_id]:
            selectedTemplateName ||
            response.data.template_name ||
            "Unknown Template",
        }));
      }

      if (response.data.task_id) {
        setTaskIds((prev) => [...prev, response.data.task_id]);
        setShowStatusDot(true);
      } else if (
        response.data.task_ids &&
        Array.isArray(response.data.task_ids)
      ) {
        setTaskIds((prev) => [...prev, ...response.data.task_ids]);
        if (response.data.task_ids.length > 0) {
          setShowStatusDot(true);
        }
      }

      // Clear completion indicators when new tasks are submitted
      setHasUnviewedCompletedTasks(false);
      setLastCompletionStatus(null);
      // No longer using status updates indicator here

      // Clear the captured images after successful upload
      setCapturedImages([]);

      // Show success notification
      setShowSuccessNotification(true);
      setTimeout(() => {
        setShowSuccessNotification(false);
      }, 3000);

      // Show processing started notification after successful upload
      console.log(
        "Dashboard: Upload successful, showing processing notification"
      );
      setShowProcessingNotification(true);
    } catch (error) {
      console.error("Error uploading images:", error.response?.data || error);
      // More specific error message
      const errorMessage =
        error.response?.data?.message || "Error uploading images";
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (taskId) => {
    setExpandedTasks((prev) => ({
      ...prev,
      [taskId]: !prev[taskId],
    }));
  };

  const formatDate = (dateString) => {
    const options = {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const getStatusColor = (state) => {
    switch (state) {
      case "SUCCESS":
        return "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30";
      case "FAILURE":
        return "bg-rose-500/20 text-rose-400 border border-rose-500/30";
      case "PROCESSING":
      case "PENDING":
      default:
        return "bg-amber-500/20 text-amber-400 border border-amber-500/30";
    }
  };

  const formatDateTime = (dateString, timeString) => {
    const options = {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    };
    return new Date(`${dateString}T${timeString}`).toLocaleString(
      undefined,
      options
    );
  };

  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
  };

  const themeClasses = useMemo(
    () =>
      isDarkMode
        ? {
            layout: "bg-gradient-to-br from-gray-900 via-black to-gray-800",
            leftPanel:
              "bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900 backdrop-blur-xl border-r border-gray-800/50",
            card: "bg-gray-900/50 backdrop-blur-xl border border-gray-800",
            text: "text-gray-200",
            subtext: "text-gray-400",
            buttonBase: "text-white",
            inputBase: "bg-gray-700 text-gray-200 border border-gray-600",
          }
        : {
            layout: "bg-gradient-to-br from-gray-100 via-white to-white",
            leftPanel:
              "bg-gradient-to-b from-white via-gray-50 to-white backdrop-blur-sm border-r border-gray-300",
            card: "bg-white/90 backdrop-blur-sm border border-gray-200",
            text: "text-gray-800",
            subtext: "text-gray-600",
            buttonBase: "text-gray-800",
            inputBase: "bg-white text-gray-800 border border-gray-300",
          },
    [isDarkMode]
  );

  // Component Definitions
  // const Button = ({ children, onClick, variant, className, disabled }) => (
  //   <button
  //     onClick={onClick}
  //     className={`px-4 py-2 rounded-lg flex items-center justify-center transition-all duration-300 ${
  //       variant === "destructive"
  //         ? isDarkMode
  //           ? "bg-gradient-to-r from-rose-500/30 to-red-500/30 hover:from-rose-500/40 hover:to-red-500/40 text-rose-400 border-rose-500/50 hover:text-rose-300 hover:border-rose-400/50 shadow-lg shadow-rose-500/10 hover:shadow-rose-500/20"
  //           : "bg-gradient-to-r from-red-400/30 to-red-300/30 hover:from-red-400/40 hover:to-red-300/40 text-red-700 border border-red-300 hover:text-red-800 shadow-md hover:shadow-lg"
  //         : variant === "outline"
  //         ? isDarkMode
  //           ? "bg-transparent border border-gray-600 text-gray-200 hover:bg-gray-700"
  //           : "bg-transparent border border-gray-300 text-gray-800 hover:bg-gray-200"
  //         : variant === "scan"
  //         ? isDarkMode
  //           ? "bg-blue-500/30 hover:bg-blue-500/40 text-blue-400 hover:text-blue-300 border border-blue-500/30 hover:border-blue-400/50 shadow-md shadow-blue-500/20 hover:shadow-blue-500/30"
  //           : "bg-blue-500/20 hover:bg-blue-500/30 text-blue-600 hover:text-blue-700 border border-blue-400/30 hover:border-blue-500/50 shadow-sm hover:shadow-md shadow-blue-500/10"
  //         : isDarkMode
  //         ? "bg-blue-500 text-white hover:bg-blue-600"
  //         : "bg-blue-600 text-white hover:bg-blue-700"
  //     } ${className}`}
  //     disabled={disabled}
  //   >
  //     {children}
  //   </button>
  // );

  const Card = ({ children, className }) => (
    <div
      className={`${themeClasses.card} rounded-xl p-6 transition-all duration-300 ${className}`}
    >
      {children}
    </div>
  );

  // const Select = ({ children, id, value, onChange }) => (
  //   <select
  //     id={id}
  //     value={value}
  //     onChange={onChange}
  //     className={`w-full px-3 py-2 rounded-lg border ${themeClasses.inputBase} focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-300`}
  //   >
  //     {children}
  //   </select>
  // );

  // const Checkbox = ({ id, checked, onChange }) => (
  //   <input
  //     type="checkbox"
  //     id={id}
  //     checked={checked}
  //     onChange={onChange}
  //     className={`form-checkbox h-5 w-5 ${
  //       isDarkMode
  //         ? "text-blue-600 bg-gray-700 border-gray-600 rounded"
  //         : "text-blue-600 bg-white border-gray-300 rounded"
  //     }`}
  //   />
  // );

  // const InputLabel = ({ htmlFor, children }) => (
  //   <label
  //     htmlFor={htmlFor}
  //     className={`block text-sm font-medium mb-1 ${themeClasses.text}`}
  //   >
  //     {children}
  //   </label>
  // );

  const removeImage = (indexToRemove) => {
    setCapturedImages((prevImages) =>
      prevImages.filter((_, index) => index !== indexToRemove)
    );
  };

  const handleCapture = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*,.pdf";
    input.multiple = true;

    // Only set capture attribute for mobile devices and only for images
    if (isMobile) {
      input.capture = "environment";
    }

    input.onchange = (e) => {
      try {
        const files = Array.from(e.target.files);
        if (!files.length) return;

        files.forEach((file) => {
          setCapturedImages((prev) => [
            ...prev,
            {
              file,
              displayImage: URL.createObjectURL(file),
              filename: file.name,
              isPdf: file.type === "application/pdf",
            },
          ]);
        });
      } catch (error) {
        console.error("Error handling capture:", error);
        alert("Failed to capture image.");
      }
    };

    input.click();
  };

  const handleEditTemplate = (template) => {
    setSelectedTemplate(template);
    setShowEditTemplate(true);
  };

  // const toggleDocExpand = (docId) => {
  //   setExpandedDocs((prev) => ({
  //     ...prev,
  //     [docId]: !prev[docId],
  //   }));
  // };

  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem("authToken");
          navigate("/");
        }
        return Promise.reject(error);
      }
    );

    // Cleanup interceptor on component unmount
    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, [navigate]);

  // Add fetchUsageStats to initial load
  useEffect(() => {
    const source = axios.CancelToken.source();

    const fetchInitialData = async () => {
      setTemplatesLoading(true);
      setStatsLoading(true);
      // We're starting the initial load - make sure initialLoading is true
      setInitialLoading(true);

      try {
        const token = localStorage.getItem("authToken");

        // Check if token exists
        if (!token) {
          navigate("/");
          return;
        }

        const headers = {
          Authorization: `Bearer ${token}`,
        };

        // Use Promise.all to fetch data concurrently
        const promises = [
          axios.get("/api/templates", { headers, cancelToken: source.token }),
          axios.get("/api/usage_stats", { headers, cancelToken: source.token }),
          axios.get("/api/payment/current-subscription", {
            headers,
            cancelToken: source.token,
          }),
          axios.get("/api/review_documents", {
            headers,
            cancelToken: source.token,
          }),
        ];

        // Add companies fetch if user has company management feature
        if (hasCompanyManagement()) {
          promises.push(
            axios.get("/api/companies", { headers, cancelToken: source.token })
          );
        }

        const responses = await Promise.all(promises);

        // Extract responses
        const templatesResponse = responses[0];
        const usageResponse = responses[1];
        const subscriptionResponse = responses[2];
        const reviewResponse = responses[3];
        const companiesResponse = hasCompanyManagement() ? responses[4] : null;

        // Process Templates
        setTemplates(templatesResponse.data);
        // No template is selected by default - user must manually select one
        // if (templatesResponse.data.length > 0) {
        //   const defaultTemplate = templatesResponse.data[0];
        //   setSelectedTemplate(defaultTemplate);
        // }

        // Process Usage Stats
        setUsageStats(usageResponse.data);
        setStatsError(null);

        // Process Subscription
        setCurrentSubscription(subscriptionResponse.data.subscription);

        // Process Review Documents
        const initDocs = reviewResponse.data.documents || [];
        setReviewDocuments(initDocs);
        const initNeedsVerification = initDocs.some(
          (d) =>
            d.flag === "needs verification" ||
            d.needs_verification === true ||
            d.isReviewed === false
        );
        setHasPendingReviewDocs(initNeedsVerification);

        // Process Companies
        if (companiesResponse) {
          const companiesData = Array.isArray(companiesResponse.data)
            ? companiesResponse.data
            : companiesResponse.data.companies || [];
          setCompanies(companiesData);
        }

        // All data has been processed and set - now we can hide the loading screen
        setInitialLoading(false);
      } catch (error) {
        console.error("Error fetching initial data:", error);

        // Ignore cancellation errors - they're expected when component unmounts
        if (axios.isCancel(error)) {
          console.log("Request cancelled:", error.message);
          return;
        }

        if (error.response?.status === 401) {
          localStorage.removeItem("authToken");
          navigate("/");
        }
        // Update specific error states if needed
        setTemplatesError(error.message);
        setStatsError(error.message);
        // Even on error, hide the loading screen
        setInitialLoading(false);
      } finally {
        setTemplatesLoading(false);
        setStatsLoading(false);
      }
    };

    fetchInitialData();

    return () => {
      source.cancel("Component got unmounted");
    };
  }, [navigate, hasCompanyManagement]);

  // Smart refresh strategy for history section
  useEffect(() => {
    if (activeSection === "history") {
      // Initial fetch when switching to history section
      fetchDocumentHistory(currentPage, searchQuery);
      // Clear the unviewed indicator when user visits history
      setHasUnviewedCompletedTasks(false);
      setLastCompletionStatus(null);
    }
  }, [activeSection, currentPage, searchQuery]);

  // Clear the status dot when user opens the status section
  useEffect(() => {
    if (activeSection === "status") {
      setShowStatusDot(false);
    }
  }, [activeSection]);

  //effect to auto-close mobile menu when switching to desktop:
  useEffect(() => {
    if (!isMobile && isMenuOpen) {
      setIsMenuOpen(false);
    }
  }, [isMobile, isMenuOpen]);

  // Refresh history after document submission
  useEffect(() => {
    // Only refresh if we just submitted documents and we're on the history section
    if (activeSection === "history" && taskIds.length > 0) {
      const refreshTimer = setTimeout(() => {
        fetchDocumentHistory(1, searchQuery, true); // Go to first page to see new documents
      }, 2000); // Wait 2 seconds after submission for processing to start

      return () => clearTimeout(refreshTimer);
    }
  }, [taskIds.length, activeSection]);

  // Manual refresh function for user-triggered updates
  const handleManualRefresh = useCallback(() => {
    if (activeSection === "history") {
      fetchDocumentHistory(currentPage, searchQuery);
      // Clear indicators when manually refreshing history
      setHasUnviewedCompletedTasks(false);
      setLastCompletionStatus(null);
    }
  }, [activeSection, currentPage, searchQuery]);

  // Keep the fetchUsageStats function for manual refreshes if needed
  const _fetchUsageStats = async () => {
    try {
      setStatsError(null);
      const token = localStorage.getItem("authToken");
      const response = await axios.get("/api/usage_stats", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setUsageStats(response.data);
    } catch (error) {
      console.error("Error fetching usage stats:", error);
      setStatsError(error.message);
    } finally {
      setStatsLoading(false);
    }
  };

  // const createChartData = (used, remaining, isDaily = true) => ({
  //   labels: ["Used", "Remaining"],
  //   datasets: [
  //     {
  //       data: [used, remaining],
  //       backgroundColor: isDarkMode
  //         ? [
  //             isDaily ? "rgba(56, 189, 248, 0.3)" : "rgba(168, 85, 247, 0.3)",
  //             "rgba(31, 41, 55, 0.5)",
  //           ]
  //         : [
  //             isDaily ? "rgba(56, 189, 248, 0.5)" : "rgba(168, 85, 247, 0.5)",
  //             "rgba(243, 244, 246, 0.5)",
  //           ],
  //       borderColor: isDarkMode
  //         ? [
  //             isDaily ? "rgba(56, 189, 248, 0.8)" : "rgba(168, 85, 247, 0.8)",
  //             "rgba(75, 85, 99, 0.3)",
  //           ]
  //         : [
  //             isDaily ? "rgba(56, 189, 248, 1)" : "rgba(168, 85, 247, 1)",
  //             "rgba(229, 231, 235, 1)",
  //           ],
  //       borderWidth: 2,
  //     },
  //   ],
  // });

  // const UsageStats = () => (
  //   <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
  //     {statsLoading ? (
  //       <Card className="p-4">
  //         <p className={`${themeClasses.text}`}>Loading usage stats...</p>
  //       </Card>
  //     ) : statsError ? (
  //       <Card className="p-4">
  //         <p className="text-red-500">Error loading stats: {statsError}</p>
  //       </Card>
  //     ) : usageStats ? (
  //       <>
  //         <Card className="p-4">
  //           <h3 className={`text-lg font-semibold mb-2 ${themeClasses.text}`}>
  //             Today's Usage
  //           </h3>
  //           <div className={`space-y-2 ${themeClasses.text}`}>
  //             <p>Scanned: {usageStats.today.count}</p>
  //             <p>Remaining: {usageStats.today.remaining}</p>
  //             <div className="w-full bg-gray-200 rounded-full h-2.5 dark:bg-gray-700">
  //               <div
  //                 className="bg-blue-600 h-2.5 rounded-full"
  //                 style={{
  //                   width: `${
  //                     (usageStats.today.count / usageStats.today.limit) * 100
  //                   }%`,
  //                 }}
  //               ></div>
  //             </div>
  //           </div>
  //         </Card>

  //         <Card className="p-4">
  //           <h3 className={`text-lg font-semibold mb-2 ${themeClasses.text}`}>
  //             Monthly Usage
  //           </h3>
  //           <div className={`space-y-2 ${themeClasses.text}`}>
  //             <p>Scanned: {usageStats.month.count}</p>
  //             <p>Remaining: {usageStats.month.remaining}</p>
  //             <div className="w-full bg-gray-200 rounded-full h-2.5 dark:bg-gray-700">
  //               <div
  //                 className="bg-blue-600 h-2.5 rounded-full"
  //                 style={{
  //                   width: `${
  //                     (usageStats.month.count / usageStats.month.limit) * 100
  //                   }%`,
  //                 }}
  //               ></div>
  //             </div>
  //           </div>
  //         </Card>
  //       </>
  //     ) : null}
  //   </div>
  // );

  const fetchTemplates = useCallback(async () => {
    setTemplatesLoading(true);
    try {
      const token = localStorage.getItem("authToken");

      // Check if token exists
      if (!token) {
        navigate("/");
        return;
      }

      const response = await axios.get("/api/templates", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setTemplates(response.data);

      // No template is selected by default - user must manually select one
      // if (response.data.length > 0 && !selectedTemplate) {
      //   setSelectedTemplate(response.data[0]);
      // }
    } catch (error) {
      console.error("Error fetching templates:", error);
      // Handle 401 unauthorized error
      if (error.response?.status === 401) {
        localStorage.removeItem("authToken");
        navigate("/");
      }
    } finally {
      setTemplatesLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchTemplates();
  }, [navigate]);

  const handleTemplateCreated = async () => {
    setShowCreateTemplate(false);
    await fetchTemplates();
    toast.success("Template created successfully!");
  };

  const handleTemplateUpdated = async () => {
    setShowEditTemplate(false);
    setSelectedTemplate(null);
    await fetchTemplates();
    toast.success("Template updated successfully!");
  };

  const _handleDeleteTemplate = async (templateId) => {
    if (!window.confirm("Are you sure you want to delete this template?")) {
      return;
    }

    try {
      const token = localStorage.getItem("authToken");
      await axios.delete(`/api/templates/${templateId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // Refresh templates list
      setTemplates(templates.filter((t) => t.id !== templateId));
    } catch (err) {
      console.error("Error deleting template:", err);
      alert("Failed to delete template");
    }
  };

  // Update the task status monitoring
  useEffect(() => {
    if (activeSection !== "status" || taskIds.length === 0) return;

    // Initial fetch
    fetchTaskStatuses();

    // Set up interval for periodic updates
    const intervalId = setInterval(fetchTaskStatuses, 2000);

    // Cleanup on unmount or when taskIds/activeSection changes
    return () => clearInterval(intervalId);
  }, [taskIds, activeSection]); // Add activeSection to dependencies

  useEffect(() => {
    // Update template name cache when statuses change
    statuses.forEach((status) => {
      if (
        status.template_id &&
        status.template_name &&
        !templateNameCache[status.template_id]
      ) {
        setTemplateNameCache((prev) => ({
          ...prev,
          [status.template_id]: status.template_name,
        }));
      }
    });
  }, [statuses]);

  const _getTemplateNameFromCache = (templateId, templateName) => {
    return templateNameCache[templateId] || templateName || "Loading...";
  };

  function displayJsonAsText(jsonData, indent = "") {
    // If jsonData is a string, remove code fences if present and try to parse it as JSON.
    if (typeof jsonData === "string") {
      let cleanedData = jsonData.trim();
      if (cleanedData.startsWith("```")) {
        const regex = /^```(?:\s*json\s*)?\n?([\s\S]*?)\n?```$/m;
        const match = cleanedData.match(regex);
        if (match) {
          cleanedData = match[1].trim();
        }
      }
      try {
        jsonData = JSON.parse(cleanedData);
      } catch (error) {
        // If parsing fails, use the cleaned string directly.
        jsonData = cleanedData;
      }
    }

    let lines = "";

    if (typeof jsonData === "object" && jsonData !== null) {
      if (Array.isArray(jsonData)) {
        // For each element in the array, process recursively and separate items by a divider.
        jsonData.forEach((item, index) => {
          lines += displayJsonAsText(item, indent) + "\n";
          if (index < jsonData.length - 1) {
            lines += indent + "----\n";
          }
        });
      } else {
        // For a plain object, process each key/value pair.
        for (let key in jsonData) {
          if (Object.prototype.hasOwnProperty.call(jsonData, key)) {
            let value = jsonData[key];
            if (typeof value === "object" && value !== null) {
              lines +=
                `${indent}${key}:\n` +
                displayJsonAsText(value, indent + "    ");
            } else {
              lines += `${indent}${key}: ${value}\n`;
            }
          }
        }
      }
    } else {
      // For non-object types, display as string.
      lines += indent + String(jsonData) + "\n";
    }
    return lines;
  }

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        accountDropdownRef.current &&
        !accountDropdownRef.current.contains(event.target)
      ) {
        setShowAccountDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Add to your imports
  // const [paymentLoading, setPaymentLoading] = useState(false);

  // Add this function to handle payment
  const _handlePaymentInitiation = async (plan, billingCycle) => {
    try {
      setPaymentLoading(true);
      const token = localStorage.getItem("authToken");

      const { data } = await axios.post(
        "/api/payment/dodo/checkout",
        { plan, billing_cycle: billingCycle },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (data && data.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }

      throw new Error("No checkout_url in response");
    } catch (error) {
      console.error("Payment initiation failed:", error);
      alert("Failed to initiate payment. Please try again.");
    } finally {
      setPaymentLoading(false);
    }
  };

  // Check the URL query for the payment success flag on component mount
  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    if (queryParams.get("popup") === "payment_success") {
      setShowPaymentSuccessModal(true);
      triggerConfetti(); // Trigger the confetti animation
      queryParams.delete("popup");
      const newUrl =
        window.location.pathname +
        (queryParams.toString() ? "?" + queryParams.toString() : "");
      window.history.replaceState({}, document.title, newUrl);
    } else if (queryParams.get("popup") === "payment_failed") {
      setShowPaymentFailedModal(true);
      queryParams.delete("popup");
      const newUrl =
        window.location.pathname +
        (queryParams.toString() ? "?" + queryParams.toString() : "");
      window.history.replaceState({}, document.title, newUrl);
    }
  }, []);

  // Add this function to trigger the confetti animation
  const triggerConfetti = () => {
    const duration = 3000;
    const end = Date.now() + duration;

    const colors = ["#FF6B6B", "#4ECDC4", "#45B7D1", "#96CEB4", "#FFEEAD"];

    (function frame() {
      confetti({
        particleCount: 5,
        angle: 60,
        spread: 55,
        origin: { x: 0 },
        colors: colors,
      });
      confetti({
        particleCount: 5,
        angle: 120,
        spread: 55,
        origin: { x: 1 },
        colors: colors,
      });

      if (Date.now() < end) {
        requestAnimationFrame(frame);
      }
    })();
  };

  const _handleUpgrade = () => {
    // Show upgrade modal
    setShowUpgradeModal(true);
  };

  const _PLAN_FEATURES = {
    free: {
      name: "Free",
      features: [
        "Up to 50 scans/month",
        "Up to 1 MB file size",
        "Basic features",
        "Email support",
      ],
      icon: Package,
    },
    starter: {
      name: "Starter",
      features: [
        "Up to 1,000 scans/month",
        "Up to 5 MB file size",
        "API integration",
        "Priority support",
      ],
      icon: Star,
    },
    professional: {
      name: "Pro",
      features: [
        "Up to 5,000 scans/month",
        "Up to 25 MB file size",
        "Custom integrations",
        "Document AI Agent",
        "Advanced AI analytics",
        "Priority support",
      ],
      icon: Shield,
      popular: true,
    },
    enterprise: {
      name: "Enterprise",
      features: [
        "Custom number of scans",
        "Custom AI workflows",
        "24/7 dedicated support",
        "Custom deployment",
        "Enhanced security",
      ],
      icon: Building,
    },
  };

  // Fetch API keys on component mount
  useEffect(() => {
    const fetchApiKeys = async () => {
      try {
        const token = localStorage.getItem("authToken");
        const response = await axios.get("/api/keys", {
          headers: { Authorization: `Bearer ${token}` },
        });
        setApiKeys(response.data.api_keys);
      } catch (error) {
        console.error("Error fetching API keys:", error);
      }
    };

    fetchApiKeys();
  }, []);

  const handleCreateApiKey = async (e) => {
    e.preventDefault();
    setKeyCreationLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.post(
        "/api/keys",
        { name: newKeyName },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setApiKeys([...apiKeys, response.data]);
      setShowCreateKeyModal(false);
      setNewKeyName("");
    } catch (error) {
      console.error("Error creating API key:", error);
    } finally {
      setKeyCreationLoading(false);
    }
  };

  const _handleDeleteApiKey = async (keyId) => {
    if (window.confirm("Are you sure you want to delete this API key?")) {
      try {
        const token = localStorage.getItem("authToken");
        await axios.delete(`/api/keys/${keyId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setApiKeys(apiKeys.filter((key) => key.id !== keyId));
      } catch (error) {
        console.error("Error deleting API key:", error);
      }
    }
  };

  const handleSheetSelect = (sheetName) => {
    setSelectedSheets((prev) => {
      if (prev.includes(sheetName)) {
        // Remove if already selected
        return prev.filter((s) => s !== sheetName);
      } else {
        // Add to selection
        return [...prev, sheetName];
      }
    });
  };

  const handleConfirmSheetSelection = async () => {
    if (selectedSheets.length === 0) {
      alert("Please select at least one sheet");
      return;
    }

    try {
      const reader = new FileReader();
      reader.onload = (e) => {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });

        // Convert each selected sheet to CSV
        selectedSheets.forEach((sheetName) => {
          const worksheet = workbook.Sheets[sheetName];
          const csv = XLSX.utils.sheet_to_csv(worksheet);

          // Create a new File object from the CSV
          const csvBlob = new Blob([csv], { type: "text/csv" });
          const csvFile = new File(
            [csvBlob],
            `${currentExcelFile.name.split(".")[0]}_${sheetName}.csv`,
            {
              type: "text/csv",
            }
          );

          // Add to captured images
          setCapturedImages((prev) => [
            ...prev,
            {
              file: csvFile,
              displayImage: null,
              filename: csvFile.name,
              isSpreadsheet: true,
              isCsv: true, // Mark as CSV for icon display
            },
          ]);
        });

        // Reset excel state
        setShowSheetSelector(false);
        setExcelSheets([]);
        setCurrentExcelFile(null);
        setSelectedSheets([]);
      };
      reader.readAsArrayBuffer(currentExcelFile);
    } catch (error) {
      console.error("Error processing Excel sheets:", error);
      alert("Failed to process Excel sheets");
    }
  };

  const handleCancelSheetSelection = () => {
    setShowSheetSelector(false);
    setExcelSheets([]);
    setCurrentExcelFile(null);
    setSelectedSheets([]);
  };

  const SheetSelectorModal = () => (
    <div className="fixed inset-0 flex items-center justify-center z-50">
      <div
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={handleCancelSheetSelection}
      />
      <div
        className={`relative rounded-lg p-6 shadow-xl max-w-md w-full mx-4 ${
          isDarkMode
            ? "bg-gray-800 border border-gray-700"
            : "bg-white border border-gray-200"
        }`}
      >
        <h3 className={`text-xl font-semibold mb-2 ${themeClasses.text}`}>
          Select Sheets
        </h3>
        <p className={`text-sm mb-4 ${themeClasses.subtext}`}>
          Select one or more sheets to convert to CSV
        </p>
        <div className="space-y-2 mb-6 max-h-96 overflow-y-auto">
          {excelSheets.map((sheet) => (
            <label
              key={sheet}
              className={`flex items-center p-3 rounded-lg cursor-pointer transition-colors ${
                selectedSheets.includes(sheet)
                  ? isDarkMode
                    ? "bg-blue-500/20 border-2 border-blue-500"
                    : "bg-blue-50 border-2 border-blue-500"
                  : isDarkMode
                  ? "hover:bg-gray-700 bg-gray-800 border border-gray-700"
                  : "hover:bg-gray-100 bg-white border border-gray-200"
              }`}
            >
              <input
                type="checkbox"
                checked={selectedSheets.includes(sheet)}
                onChange={() => handleSheetSelect(sheet)}
                className={`form-checkbox h-5 w-5 rounded ${
                  isDarkMode
                    ? "text-blue-600 bg-gray-700 border-gray-600"
                    : "text-blue-600 bg-white border-gray-300"
                } focus:ring-2 focus:ring-blue-500 focus:ring-offset-0`}
              />
              <span className={`ml-3 ${themeClasses.text}`}>{sheet}</span>
            </label>
          ))}
        </div>
        
        <div className="flex justify-between items-center">
          <span className={`text-sm ${themeClasses.subtext}`}>
            {selectedSheets.length} sheet{selectedSheets.length !== 1 ? 's' : ''} selected
          </span>
          <div className="flex gap-3">
            <button
              onClick={handleCancelSheetSelection}
              className={`px-4 py-2 rounded-lg transition-colors ${
                isDarkMode
                  ? "text-gray-400 hover:text-gray-200 hover:bg-gray-700"
                  : "text-gray-600 hover:text-gray-800 hover:bg-gray-100"
              }`}
            >
              Cancel
            </button>
            <button
              onClick={handleConfirmSheetSelection}
              disabled={selectedSheets.length === 0}
              className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                selectedSheets.length === 0
                  ? isDarkMode
                    ? "bg-gray-700 text-gray-500 cursor-not-allowed"
                    : "bg-gray-200 text-gray-400 cursor-not-allowed"
                  : isDarkMode
                  ? "bg-blue-600 hover:bg-blue-700 text-white"
                  : "bg-blue-600 hover:bg-blue-700 text-white"
              }`}
            >
              Confirm
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const integration = urlParams.get("integration");
    const status = urlParams.get("status");

    if (integration === "google-sheets" && status === "success") {
      setActiveIntegration("google-sheets");
      toast.success("Google Sheets connected successfully!");
    } else if (integration === "google-sheets" && status === "error") {
      toast.error(
        "Failed to connect Google Sheets: " + urlParams.get("message")
      );
    }
  }, []);

  const toggleResultViewMode = (itemId) => {
    setResultViewMode((prev) => ({
      ...prev,
      [itemId]: prev[itemId] === "json" ? "formatted" : "json",
    }));
  };

  // Function to copy JSON data to clipboard
  const copyToClipboard = (data) => {
    try {
      // If data is a string that contains JSON, parse it first
      let jsonData = data;
      if (typeof data === "string") {
        try {
          // Clean if it's a string with markdown fences
          let cleanedData = data.trim();
          if (cleanedData.startsWith("```")) {
            const match = cleanedData.match(
              /^```(?:json)?\s*([\s\S]*?)\s*```$/
            );
            if (match) cleanedData = match[1];
          }
          jsonData = JSON.parse(cleanedData);
        } catch (e) {
          // If parsing fails, use the original string
          jsonData = data;
        }
      }

      // Convert to formatted JSON string
      const jsonStr = JSON.stringify(jsonData, null, 2);

      // Use the Clipboard API
      navigator.clipboard
        .writeText(jsonStr)
        .then(() => {
          alert("Data copied to clipboard!");
        })
        .catch((err) => {
          console.error("Failed to copy: ", err);
          alert("Failed to copy to clipboard. See console for details.");
        });
    } catch (error) {
      console.error("Error copying to clipboard:", error);
      alert("Error copying to clipboard");
    }
  };

  // Function to convert JSON to CSV and download
  const downloadAsCSV = (data) => {
    try {
      // If data is a string that contains JSON, parse it first
      let jsonData = data;
      if (typeof data === "string") {
        try {
          // Clean if it's a string with markdown fences
          let cleanedData = data.trim();
          if (cleanedData.startsWith("```")) {
            const match = cleanedData.match(
              /^```(?:json)?\s*([\s\S]*?)\s*```$/
            );
            if (match) cleanedData = match[1];
          }
          jsonData = JSON.parse(cleanedData);
        } catch (e) {
          // If parsing fails, use the original string
          alert("Cannot parse data as JSON");
          return;
        }
      }

      // Function to properly escape CSV values
      const escapeCSVValue = (value) => {
        if (value === null || value === undefined) {
          return "";
        }

        const stringValue = String(value);

        // If the value contains comma, newline, or double quote, wrap in quotes and escape internal quotes
        if (
          stringValue.includes(",") ||
          stringValue.includes("\n") ||
          stringValue.includes("\r") ||
          stringValue.includes('"')
        ) {
          return `"${stringValue.replace(/"/g, '""')}"`;
        }

        return stringValue;
      };

      // Function to flatten non-array data for single row
      const flattenSingleRow = (obj, prefix = "") => {
        const result = {};

        Object.keys(obj).forEach((key) => {
          const pre = prefix.length ? `${prefix}_${key}` : key;
          const value = obj[key];

          if (value === null || value === undefined) {
            result[pre] = "";
          } else if (Array.isArray(value)) {
            // For arrays, we'll handle them separately in tabular format
            if (value.length === 0) {
              result[pre] = "";
            } else if (
              value.every((item) => typeof item === "object" && item !== null)
            ) {
              // Skip arrays of objects - they'll be handled as separate tables
              result[pre] = "";
            } else {
              // Simple array - join with semicolon
              result[pre] = value
                .map((item) => escapeCSVValue(item))
                .join("; ");
            }
          } else if (typeof value === "object" && value !== null) {
            // Recursively flatten nested objects
            Object.assign(result, flattenSingleRow(value, pre));
          } else {
            result[pre] = value;
          }
        });

        return result;
      };

      // Create CSV content
      let csv = "";
      
      // Add BOM for proper UTF-8 encoding in Excel
      csv += "\uFEFF";

      // First, handle the main object as a single row (excluding arrays of objects)
      const singleRowData = flattenSingleRow(jsonData);
      const singleRowHeaders = Object.keys(singleRowData).filter(key => singleRowData[key] !== "");
      
      if (singleRowHeaders.length > 0) {
        // Add single row headers
        csv += singleRowHeaders.map((header) => escapeCSVValue(header)).join(",") + "\r\n";
        
        // Add single row data
        const singleRowValues = singleRowHeaders.map((header) =>
          escapeCSVValue(singleRowData[header])
        );
        csv += singleRowValues.join(",") + "\r\n";
      }

      // Now handle arrays of objects as separate tables
      const handleArrayOfObjects = (arrayData, arrayKey) => {
        if (!Array.isArray(arrayData) || arrayData.length === 0) return;
        
        // Get all possible keys from all objects in the array
        const allKeys = [...new Set(arrayData.flatMap(obj => Object.keys(obj)))];
        
        if (allKeys.length === 0) return;
        
        // Add empty line separator
        csv += "\r\n";
        
        // Add section header
        csv += escapeCSVValue(`${arrayKey} Data`) + "\r\n";
        
        // Add column headers
        csv += allKeys.map((key) => escapeCSVValue(key)).join(",") + "\r\n";
        
        // Add data rows
        arrayData.forEach((item) => {
          const rowValues = allKeys.map((key) => {
            const value = item[key];
            return escapeCSVValue(value);
          });
          csv += rowValues.join(",") + "\r\n";
        });
      };

      // Process all arrays of objects in the JSON data
      Object.keys(jsonData).forEach((key) => {
        const value = jsonData[key];
        if (Array.isArray(value) && 
            value.length > 0 && 
            value.every((item) => typeof item === "object" && item !== null)) {
          handleArrayOfObjects(value, key);
        }
      });

      // Create and trigger download
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `document_${new Date().toISOString().slice(0, 10)}.csv`
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error downloading CSV:", error);
      alert("Error downloading CSV");
    }
  };

  const fetchDocumentHistory = useCallback(
    async (page = 1, search = "", resetPage = false, filterOverrides = null) => {
      try {
        setIsLoadingHistory(true);
        const token = localStorage.getItem("authToken");

        // If resetPage is true, always use page 1
        const actualPage = resetPage ? 1 : page;
        const startDate =
          filterOverrides?.startDate ?? historyStartDateRef.current;
        const endDate = filterOverrides?.endDate ?? historyEndDateRef.current;
        const statusFilter =
          filterOverrides?.statusFilter ?? historyStatusFilterRef.current;

        const params = new URLSearchParams({
          page: actualPage.toString(),
          limit: "15",
          ...(search && { search }),
          ...(startDate && { start_date: startDate }),
          ...(endDate && { end_date: endDate }),
          ...(statusFilter && statusFilter !== "all" && { status_filter: statusFilter }),
        });

        const response = await axios.get(`/api/document_history?${params}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        // Update pagination state
        if (response.data.pagination) {
          setCurrentPage(response.data.pagination.current_page);
          setTotalPages(response.data.pagination.total_pages);
          setTotalDocuments(response.data.pagination.total_documents);
          setPaginationInfo(response.data.pagination);
        }

        // Update document history
        setDocumentHistory(response.data.history || []);
        setHistoryStatusCounts(response.data.status_counts || null);
      } catch (error) {
        console.error("Error fetching document history:", error);
        setDocumentHistory([]);
        setHistoryStatusCounts(null);
      } finally {
        setIsLoadingHistory(false);
      }
    },
    []
  );

  const handleHistoryDateFilterChange = useCallback(
    (startDate, endDate) => {
      const nextStartDate = startDate || "";
      const nextEndDate = endDate || "";

      if (nextStartDate && nextEndDate && nextStartDate > nextEndDate) {
        toast.error("Start date cannot be after end date.");
        return;
      }

      setHistoryStartDate(nextStartDate);
      setHistoryEndDate(nextEndDate);
      fetchDocumentHistory(1, searchQuery, true, {
        startDate: nextStartDate,
        endDate: nextEndDate,
      });
    },
    [fetchDocumentHistory, searchQuery]
  );

  const handleHistoryStatusFilterChange = useCallback(
    (statusFilter) => {
      const nextStatusFilter = statusFilter || "all";
      setHistoryStatusFilter(nextStatusFilter);
      fetchDocumentHistory(1, searchQuery, true, {
        statusFilter: nextStatusFilter,
      });
    },
    [fetchDocumentHistory, searchQuery]
  );

  const exportHistoryToExcel = useCallback(async () => {
    try {
      setIsExportingHistoryExcel(true);
      const token = localStorage.getItem("authToken");

      if (!token) {
        toast.error("Authentication token not found.");
        return;
      }

      if (
        historyStartDateRef.current &&
        historyEndDateRef.current &&
        historyStartDateRef.current > historyEndDateRef.current
      ) {
        toast.error("Start date cannot be after end date.");
        return;
      }

      const params = new URLSearchParams({
        ...(searchQuery && { search: searchQuery }),
        ...(historyStartDateRef.current && {
          start_date: historyStartDateRef.current,
        }),
        ...(historyEndDateRef.current && { end_date: historyEndDateRef.current }),
        ...(historyStatusFilterRef.current &&
          historyStatusFilterRef.current !== "all" && {
            status_filter: historyStatusFilterRef.current,
          }),
      });

      const response = await axios.get(
        `/api/document_history/export?${params}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          responseType: "blob",
        }
      );

      const contentType = response.headers["content-type"] || "";
      if (contentType.includes("application/json")) {
        const errorText = await response.data.text();
        const errorData = JSON.parse(errorText);
        if (response.status === 404) {
          toast.info(errorData.error || "No history data found for selected filters.");
        } else {
          toast.error(errorData.error || "Failed to export history to Excel.");
        }
        return;
      }

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      let filename = `history_export_${new Date().toISOString().slice(0, 10)}.xlsx`;
      const contentDisposition = response.headers["content-disposition"];
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^";\n]+)"?/);
        if (filenameMatch?.[1]) {
          filename = filenameMatch[1];
        }
      }

      link.setAttribute("download", filename);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success("History exported to Excel successfully.");
    } catch (error) {
      console.error("Error exporting history to Excel:", error);

      if (error.response?.data instanceof Blob) {
        try {
          const errorText = await error.response.data.text();
          const errorData = JSON.parse(errorText);
          if (error.response.status === 404) {
            toast.info(errorData.error || "No history data found for selected filters.");
          } else {
            toast.error(errorData.error || "Failed to export history to Excel.");
          }
          return;
        } catch (_parseError) {
          // Fall through to generic error toast.
        }
      }

      toast.error("Failed to export history to Excel.");
    } finally {
      setIsExportingHistoryExcel(false);
    }
  }, [searchQuery]);

  // Delete document function
  const handleDeleteDocument = async (documentId) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this document? This action cannot be undone."
      )
    ) {
      return;
    }

    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.delete(
        `/api/document_history/${documentId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200) {
        // Remove the document from the local state
        setDocumentHistory((prev) =>
          prev.filter((doc) => doc.id !== documentId)
        );

        // Show success message
        toast.success("Document deleted successfully");
      }
    } catch (error) {
      console.error("Error deleting document:", error);
      toast.error("Failed to delete document. Please try again.");
    }
  };

  // Reprocess document function
  const _handleReprocessDocument = async (doc) => {
    if (
      !window.confirm(
        "Are you sure you want to reprocess this document? This will create a new processing task."
      )
    ) {
      return;
    }

    // Convert S3 URL to CloudFront URL
    const convertS3ToCloudFrontUrl = (s3Url) => {
      if (!s3Url || !s3Url.startsWith("s3://")) {
        return s3Url; // Return as is if not an S3 URL
      }

      try {
        // Extract the key from S3 URL (e.g., s3://clevrscan/users/244/file.png -> users/244/file.png)
        const s3Key = s3Url.replace(/^s3:\/\/[^/]+\//, "");

        // Get CloudFront domain from environment variables
        const cloudFrontDomain =
          process.env.CLOUDFRONT_DOMAIN || "d2zlfym0sm19sj.cloudfront.net";

        return `https://${cloudFrontDomain}/${s3Key}`;
      } catch (error) {
        console.error("Error converting S3 URL to CloudFront URL:", error);
        return s3Url; // Fallback to original URL
      }
    };

    try {
      setReprocessingDocuments((prev) => ({ ...prev, [doc.id]: true }));

      const token = localStorage.getItem("authToken");
      const cloudFrontUrl = convertS3ToCloudFrontUrl(doc.url);

      // Use ONLY the original template ID from the document
      if (!doc.template) {
        throw new Error(
          "No template information found for this document. Cannot reprocess."
        );
      }

      const templateId = doc.template;

      // Get company ID if user has company management feature and a company is selected
      const companyId =
        hasCompanyManagement() && selectedCompany ? selectedCompany.id : null;

      const payload = {
        file_urls: [
          {
            url: cloudFrontUrl,
            filename: doc.name,
          },
        ],
        template: templateId,
        batchProcess: false,
        splitPdf: false,
        ...(companyId && { company_id: companyId }),
      };

      // Use the tally upload OCR API endpoint
      const response = await axios.post(
        "/api/async/tally/upload_ocr",
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 202) {
        // Add new task to tracking
        if (response.data.task_id) {
          setTaskIds((prev) => [...prev, response.data.task_id]);
          setShowStatusDot(true);
        } else if (
          response.data.task_ids &&
          Array.isArray(response.data.task_ids)
        ) {
          setTaskIds((prev) => [...prev, ...response.data.task_ids]);
          if (response.data.task_ids.length > 0) {
            setShowStatusDot(true);
          }
        }

        // Show success message
        toast.success(
          "Document reprocessing started! Check the Status section for progress."
        );

        // Optionally switch to status section
        setActiveSection("status");
      }
    } catch (error) {
      console.error("Error reprocessing document:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to reprocess document";
      toast.error(`Failed to reprocess document: ${errorMessage}`);
    } finally {
      setReprocessingDocuments((prev) => ({ ...prev, [doc.id]: false }));
    }
  };

  // Fetch presigned URL for a specific document
  const _fetchPresignedUrl = async (documentId) => {
    // Check if we already have the URL or are currently loading it
    if (presignedUrls[documentId] || loadingUrls[documentId]) {
      return presignedUrls[documentId];
    }

    setLoadingUrls((prev) => ({ ...prev, [documentId]: true }));

    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.get(
        `/api/document/${documentId}/presigned_url`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200) {
        const { presigned_url } = response.data;
        setPresignedUrls((prev) => ({ ...prev, [documentId]: presigned_url }));
        return presigned_url;
      }
    } catch (error) {
      console.error(
        `Error fetching presigned URL for document ${documentId}:`,
        error
      );
      toast.error("Failed to load document preview");
    } finally {
      setLoadingUrls((prev) => ({ ...prev, [documentId]: false }));
    }

    return null;
  };

  // Search input ref to maintain focus
  // const searchInputRef = useRef(null);

  // Isolated Search Input Component to prevent re-render issues
  const SearchInput = React.memo(
    ({
      onSearchChange,
      onClearSearch,
      isDarkMode,
      searchResultCount,
      hasSearchQuery,
      isLoading,
    }) => {
      const [inputValue, setInputValue] = useState("");
      const timeoutRef = useRef(null);
      const inputRef = useRef(null);

      const handleChange = useCallback(
        (e) => {
          const value = e.target.value;
          setInputValue(value);

          // Clear existing timeout
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
          }

          // Set new timeout for debounced search
          timeoutRef.current = setTimeout(() => {
            onSearchChange(value);
          }, 500);
        },
        [onSearchChange]
      );

      const handleClear = useCallback(() => {
        setInputValue("");
        onClearSearch();
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        // Keep focus on input after clearing
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, [onClearSearch]);

      // Cleanup timeout on unmount
      useEffect(() => {
        return () => {
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
          }
        };
      }, []);

      return (
        <div className="mb-6">
          <div className="relative">
            <Search
              className={`absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none h-6 w-6 ${
                isDarkMode ? "text-gray-400" : "text-gray-500"
              }`}
              style={{
                top: "50%",
                transform: "translateY(-50%)",
                left: "12px",
              }}
            />
            <input
              ref={inputRef}
              type="text"
              placeholder="Search documents by name, template, status, or date..."
              value={inputValue}
              onChange={handleChange}
              className={`
              w-full pl-12 pr-${
                inputValue ? "20" : "4"
              } py-3 rounded-lg border transition-all duration-300
              ${
                isDarkMode
                  ? "bg-gray-800 border-gray-600 text-gray-200 placeholder-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                  : "bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
              }
              focus:outline-none
            `}
            />
            {inputValue && (
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 space-x-2">
                <button
                  onClick={handleClear}
                  className={`p-1 rounded-full transition-all duration-200 ${
                    isDarkMode
                      ? "text-blue-400 hover:bg-blue-500/20 hover:text-blue-300"
                      : "text-blue-600 hover:bg-blue-100 hover:text-blue-700"
                  }`}
                  title="Show all documents"
                >
                  <span className="text-xs font-medium px-2 py-1 rounded-md bg-current/10">
                    Show All
                  </span>
                </button>
                <button
                  onClick={handleClear}
                  className={`p-1 rounded-full transition-all duration-200 ${
                    isDarkMode
                      ? "text-gray-400 hover:text-gray-200"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                  title="Clear search"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
          {hasSearchQuery && (
            <div className="flex items-center justify-between mt-2">
              <p
                className={`text-sm ${
                  isDarkMode ? "text-gray-400" : "text-gray-600"
                }`}
              >
                {isLoading ? (
                  <span className="flex items-center">
                    <div className="relative w-7 h-7">
                      <motion.div
                        className="w-full h-full"
                        animate={{ rotate: 360 }}
                        transition={{
                          duration: 2,
                          ease: "linear",
                          repeat: Infinity,
                        }}
                      >
                        <img
                          src={favicon}
                          alt="Loading"
                          className="w-full h-full object-contain"
                          onError={(e) => {
                            console.log("Image failed to load:", e.target.src);
                            e.target.style.display = "none";
                          }}
                        />
                      </motion.div>
                    </div>
                    <span>Searching...</span>
                  </span>
                ) : searchResultCount === 0 ? (
                  <span className="flex items-center">
                    <span>No documents found</span>
                    {inputValue && (
                      <span className="ml-1">matching "{inputValue}"</span>
                    )}
                  </span>
                ) : (
                  <span>
                    Found {searchResultCount} document
                    {searchResultCount !== 1 ? "s" : ""}
                    {inputValue && (
                      <span className="ml-1">matching "{inputValue}"</span>
                    )}
                  </span>
                )}
              </p>
              <button
                onClick={handleClear}
                className={`text-sm px-3 py-1 rounded-md transition-all duration-200 ${
                  isDarkMode
                    ? "text-blue-400 hover:bg-blue-500/20 bg-blue-500/10"
                    : "text-blue-600 hover:bg-blue-100 bg-blue-50"
                }`}
              >
                {searchResultCount === 0
                  ? "Back to All Documents"
                  : "Show All Documents"}
              </button>
            </div>
          )}
        </div>
      );
    }
  );

  SearchInput.displayName = "SearchInput";
  // MediaViewer component for displaying images and PDFs
  const MediaViewer = ({
    doc,
    isDarkMode,
    presignedUrls = {},
    loadingUrls = {},
    fetchPresignedUrls,
  }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [imageLoaded, setImageLoaded] = useState(false);
    const [imageError, setImageError] = useState(false);

    // Get view mode for this specific document (default to 'data')
    const viewMode = documentViewModes[doc.id] || "data";

    // Handle ESC key to close modal
    useEffect(() => {
      const handleKeyDown = (event) => {
        if (event.key === "Escape" && isModalOpen) {
          closeModal();
        }
      };

      if (isModalOpen) {
        document.addEventListener("keydown", handleKeyDown);
        // Prevent body scroll when modal is open
        document.body.style.overflow = "hidden";
      }

      return () => {
        document.removeEventListener("keydown", handleKeyDown);
        document.body.style.overflow = "unset";
      };
    }, [isModalOpen]);

    // Only show media in image view mode for this document
    if (viewMode !== "image") {
      return null;
    }

    // More flexible media check
    const hasMedia = doc.has_media || doc.media || doc.file_type;
    if (!hasMedia) {
      return null;
    }

    const handleImageLoad = () => {
      setImageLoaded(true);
      setImageError(false);
    };

    const handleImageError = () => {
      setImageError(true);
      setImageLoaded(false);
    };

    const openModal = async () => {
      // Fetch presigned URL if we don't have it yet and fetchPresignedUrls is available
      if (
        fetchPresignedUrls &&
        !presignedUrls[doc.id] &&
        !loadingUrls[doc.id]
      ) {
        await fetchPresignedUrls([doc.id]);
      }
      setIsModalOpen(true);
    };

    const closeModal = (e) => {
      // Prevent event bubbling
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      setIsModalOpen(false);
    };

    // Check if we have a presigned URL or are loading
    const presignedData = presignedUrls[doc.id];
    const isLoadingUrl = loadingUrls[doc.id];

    // Show loading state while fetching URL
    if (isLoadingUrl) {
      return (
        <div
          className={`mt-4 p-6 rounded-lg border-2 border-dashed ${
            isDarkMode
              ? "border-gray-600 bg-gray-800"
              : "border-gray-300 bg-gray-50"
          } flex items-center justify-center h-48`}
        >
          <div className="flex items-center space-x-2">
            <div className="relative w-7 h-7">
              <motion.div
                className="w-full h-full"
                animate={{ rotate: 360 }}
                transition={{
                  duration: 2,
                  ease: "linear",
                  repeat: Infinity,
                }}
              >
                <img
                  src={favicon}
                  alt="Loading"
                  className="w-full h-full object-contain"
                  onError={(e) => {
                    console.log("Image failed to load:", e.target.src);
                    e.target.style.display = "none";
                  }}
                />
              </motion.div>
            </div>
            <span
              className={`text-sm ${
                isDarkMode ? "text-gray-300" : "text-gray-600"
              }`}
            >
              Loading media...
            </span>
          </div>
        </div>
      );
    }

    // Show message if no presigned URL available
    if (!presignedData) {
      return (
        <div
          className={`mt-4 p-6 rounded-lg border-2 border-dashed ${
            isDarkMode
              ? "border-gray-600 bg-gray-800"
              : "border-gray-300 bg-gray-50"
          } flex items-center justify-center h-48`}
        >
          <div className="flex items-center space-x-2">
            <div className="relative w-7 h-7">
              <motion.div
                className="w-full h-full"
                animate={{ rotate: 360 }}
                transition={{
                  duration: 2,
                  ease: "linear",
                  repeat: Infinity,
                }}
              >
                <img
                  src={favicon}
                  alt="Loading"
                  className="w-full h-full object-contain"
                  onError={(e) => {
                    console.log("Image failed to load:", e.target.src);
                    e.target.style.display = "none";
                  }}
                />
              </motion.div>
            </div>
            <span
              className={`text-sm ${
                isDarkMode ? "text-gray-300" : "text-gray-600"
              }`}
            >
              Loading media...
            </span>
          </div>
        </div>
      );
    }

    const mediaUrl = presignedData.presigned_url;

    return (
      <div className="mt-4">
        <div
          className={`relative rounded-lg overflow-hidden border ${
            isDarkMode ? "border-gray-600" : "border-gray-300"
          } shadow-lg`}
        >
          {doc.file_type === "pdf" ? (
            <div className="relative">
              <iframe
                src={`${mediaUrl}#toolbar=1&navpanes=1&scrollbar=1`}
                className="w-full h-[70vh] min-h-[500px]"
                title={`PDF: ${doc.name}`}
                loading="lazy"
                allow="fullscreen"
              />
              <button
                onClick={openModal}
                className={`absolute top-4 right-4 p-3 rounded-lg ${
                  isDarkMode
                    ? "bg-gray-800/90 hover:bg-gray-700 text-white border border-gray-600"
                    : "bg-white/90 hover:bg-gray-100 text-gray-800 border border-gray-300"
                } backdrop-blur-sm transition-all duration-200 shadow-lg hover:shadow-xl`}
                title="Open in full screen"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 8V4m0 0h4m-4 0l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"
                  />
                </svg>
              </button>
            </div>
          ) : (
            <div className="relative">
              {!imageLoaded && !imageError && (
                <div
                  className={`absolute inset-0 flex items-center justify-center ${
                    isDarkMode ? "bg-gray-800" : "bg-gray-100"
                  } min-h-[400px]`}
                >
                  <div className="relative w-7 h-7">
                    <motion.div
                      className="w-full h-full"
                      animate={{ rotate: 360 }}
                      transition={{
                        duration: 2,
                        ease: "linear",
                        repeat: Infinity,
                      }}
                    >
                      <img
                        src={favicon}
                        alt="Loading"
                        className="w-full h-full object-contain"
                        onError={(e) => {
                          console.log("Image failed to load:", e.target.src);
                          e.target.style.display = "none";
                        }}
                      />
                    </motion.div>
                  </div>
                </div>
              )}
              {imageError ? (
                <div
                  className={`flex items-center justify-center h-64 ${
                    isDarkMode
                      ? "bg-gray-800 text-gray-400"
                      : "bg-gray-100 text-gray-500"
                  }`}
                >
                  <div className="text-center">
                    <svg
                      className="mx-auto h-12 w-12 mb-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                      />
                    </svg>
                    <p className="text-sm">Failed to load image</p>
                  </div>
                </div>
              ) : (
                <img
                  src={mediaUrl}
                  alt={doc.name}
                  className="w-full h-auto min-h-[400px] max-h-[70vh] object-contain cursor-pointer bg-gray-50 dark:bg-gray-900"
                  onLoad={handleImageLoad}
                  onError={handleImageError}
                  onClick={openModal}
                  style={{ imageRendering: "high-quality" }}
                />
              )}
              {imageLoaded && (
                <button
                  onClick={openModal}
                  className={`absolute top-4 right-4 p-3 rounded-lg ${
                    isDarkMode
                      ? "bg-gray-800/90 hover:bg-gray-700 text-white border border-gray-600"
                      : "bg-white/90 hover:bg-gray-100 text-gray-800 border border-gray-300"
                  } backdrop-blur-sm transition-all duration-200 shadow-lg hover:shadow-xl`}
                  title="Open in full screen"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 8V4m0 0h4m-4 0l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"
                    />
                  </svg>
                </button>
              )}
            </div>
          )}
        </div>

        {/* Enhanced Modal for full-screen view */}
        {isModalOpen && (
          <div
            className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center z-[9999] p-4"
            onClick={closeModal}
          >
            <div
              className="relative w-full h-full max-w-7xl flex items-center justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close button */}
              <button
                onClick={closeModal}
                className="absolute top-4 right-4 z-[10000] p-3 bg-black/50 hover:bg-black/70 rounded-full text-white backdrop-blur-sm transition-all duration-200 border border-white/20"
                title="Close (ESC)"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>

              {/* Content */}
              <div className="w-full h-full flex items-center justify-center">
                {doc.file_type === "pdf" ? (
                  <iframe
                    src={`${mediaUrl}#toolbar=1&navpanes=1&scrollbar=1&zoom=page-fit`}
                    className="w-full h-full rounded-lg"
                    title={`PDF: ${doc.name}`}
                    allow="fullscreen"
                  />
                ) : (
                  <img
                    src={mediaUrl}
                    alt={doc.name}
                    className="max-w-full max-h-full object-contain rounded-lg"
                    style={{ imageRendering: "high-quality" }}
                  />
                )}
              </div>

              {/* Document name overlay */}
              <div className="absolute bottom-4 left-4 right-4 text-center">
                <div className="inline-block bg-black/50 backdrop-blur-sm rounded-lg px-4 py-2 text-white text-sm border border-white/20">
                  {doc.name}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Function to fetch presigned URLs for documents when switching to image view
  const fetchPresignedUrls = async (documentIds) => {
    if (!documentIds || documentIds.length === 0) return;

    // Filter out documents that already have URLs or are currently loading
    const documentsToLoad = documentIds.filter(
      (id) => !presignedUrls[id] && !loadingUrls[id]
    );

    if (documentsToLoad.length === 0) return;

    // Mark documents as loading
    setLoadingUrls((prev) => {
      const newState = { ...prev };
      documentsToLoad.forEach((id) => (newState[id] = true));
      return newState;
    });

    try {
      const response = await axios.post(
        "/api/documents/presigned_urls",
        { document_ids: documentsToLoad },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.presigned_urls) {
        setPresignedUrls((prev) => ({
          ...prev,
          ...response.data.presigned_urls,
        }));
      }
    } catch (error) {
      console.error("Error fetching presigned URLs:", error);
    } finally {
      // Remove loading state
      setLoadingUrls((prev) => {
        const newState = { ...prev };
        documentsToLoad.forEach((id) => delete newState[id]);
        return newState;
      });
    }
  };

  // Document editing functions
  const handleEditDocument = (documentId) => {
    setEditingDocuments((prev) => ({ ...prev, [documentId]: true }));
  };

  const handleCancelEdit = (documentId) => {
    setEditingDocuments((prev) => {
      const newState = { ...prev };
      delete newState[documentId];
      return newState;
    });
  };

  const handleSaveDocument = async (documentId, updatedData) => {
    setSavingDocuments((prev) => ({ ...prev, [documentId]: true }));

    try {
      const token = localStorage.getItem("authToken");

      // Determine which endpoint to use based on current section
      const endpoint =
        activeSection === "review"
          ? `/api/review_documents/${documentId}`
          : `/api/document_history/${documentId}`;

      const response = await axios.put(
        endpoint,
        { gpt_result: updatedData },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 200) {
        // Update the document in the appropriate local state
        if (activeSection === "review") {
          setReviewDocuments((prev) =>
            prev.map((doc) =>
              doc.id === documentId
                ? {
                    ...doc,
                    gpt_result: JSON.stringify(updatedData),
                    low_confidence_count:
                      response.data.low_confidence_count ||
                      doc.low_confidence_count,
                  }
                : doc
            )
          );
        } else {
          setDocumentHistory((prev) =>
            prev.map((doc) =>
              doc.id === documentId
                ? { ...doc, gpt_result: JSON.stringify(updatedData) }
                : doc
            )
          );
        }

        // Exit edit mode
        setEditingDocuments((prev) => {
          const newState = { ...prev };
          delete newState[documentId];
          return newState;
        });

        toast.success("Document updated successfully!");
      }
    } catch (error) {
      console.error("Error saving document:", error);
      const errorMessage =
        error.response?.data?.error || "Failed to save document changes";
      toast.error(errorMessage);
    } finally {
      setSavingDocuments((prev) => {
        const newState = { ...prev };
        delete newState[documentId];
        return newState;
      });
    }
  };

  // Function to handle view mode toggle for individual documents
  const handleDocumentViewModeChange = async (documentId, mode) => {
    setDocumentViewModes((prev) => ({
      ...prev,
      [documentId]: mode,
    }));

    // If switching to image view, fetch presigned URL for this document
    if (mode === "image") {
      // Check in both filteredDocuments (history) and reviewDocuments (review)
      let doc = filteredDocuments.find((d) => d.id === documentId);
      if (!doc) {
        doc = reviewDocuments.find((d) => d.id === documentId);
      }

      // Check if document has media (be more flexible with the check)
      const hasMedia = doc?.has_media || doc?.media || doc?.file_type;

      // For review documents, we might not need to check status since they're already processed
      if (doc && hasMedia) {
        await fetchPresignedUrls([documentId]);
      }
    }
  };

  // Add missing handler functions
  const handleBatchProcessChange = (checked) => {
    setBatchProcess(checked);
  };

  const handleSplitPdfChange = (checked) => {
    setSplitPdf(checked);
  };

  const [showProcessingNotification, setShowProcessingNotification] =
    useState(false);

  // Debug notification state changes
  useEffect(() => {
    console.log(
      "Dashboard: showProcessingNotification state changed to:",
      showProcessingNotification
    );
    if (showProcessingNotification) {
      console.log("Dashboard: Notification should now be visible");
    }
  }, [showProcessingNotification]);

  return (
    <DashboardLayout
      isDarkMode={isDarkMode}
      themeClasses={themeClasses}
      initialLoading={initialLoading}
      favicon={favicon}
      sidebar={
        <DashboardSidebar
          isMobile={isMobile}
          isCollapsed={isCollapsed}
          setIsCollapsed={setIsCollapsed}
          isHovering={isHovering}
          setIsHovering={setIsHovering}
          isMenuOpen={isMenuOpen}
          setIsMenuOpen={setIsMenuOpen}
          activeSection={activeSection}
          setActiveSection={setActiveSection}
          themeClasses={themeClasses}
          isDarkMode={isDarkMode}
          hasCompanyManagement={hasCompanyManagement}
          hasFeature={hasFeature}
          hasInProgressTasks={hasInProgressTasks}
          showStatusDot={showStatusDot}
          hasUnviewedCompletedTasks={hasUnviewedCompletedTasks}
          lastCompletionStatus={lastCompletionStatus}
          hasPendingReviewDocs={hasPendingReviewDocs}
          fetchDocumentHistory={fetchDocumentHistory}
          username={username}
          showAccountDropdown={showAccountDropdown}
          setShowAccountDropdown={setShowAccountDropdown}
          accountDropdownRef={accountDropdownRef}
          favicon={favicon}
          lightlogo={lightlogo}
          darklogo={darklogo}
          handleLogout={handleLogout}
        />
      }
      header={
        <DashboardHeader
          isMobile={isMobile}
          isMenuOpen={isMenuOpen}
          setIsMenuOpen={setIsMenuOpen}
          isDarkMode={isDarkMode}
          themeClasses={themeClasses}
          activeSection={activeSection}
          toggleTheme={toggleTheme}
        />
      }
    >
      {/* Processing Notification */}
      <Notification
        message="Go to Status section"
        isVisible={showProcessingNotification}
        onClose={() => setShowProcessingNotification(false)}
        isDarkMode={isDarkMode}
      />

      <Card
        className={`p-4 sm:p-8 ${
          isDarkMode ? "bg-gray-800 bg-opacity-50" : "bg-gray-100"
        }`}
      >
        {/* Enhanced Scan Section */}
        {activeSection === "scan" && (
          <ScanSection
            // Props for state and data
            templates={templates}
            templatesLoading={templatesLoading}
            templatesError={templatesError}
            selectedTemplate={selectedTemplate}
            setSelectedTemplate={setSelectedTemplate}
            batchProcess={batchProcess}
            setBatchProcess={setBatchProcess}
            splitPdf={splitPdf}
            setSplitPdf={setSplitPdf}
            withInventoryLedger={withInventoryLedger}
            withoutInventoryLedger={withoutInventoryLedger}
            companies={companies}
            selectedCompany={selectedCompany}
            setSelectedCompany={setSelectedCompany}
            capturedImages={capturedImages}
            setCapturedImages={setCapturedImages}
            loading={loading}
            isDarkMode={isDarkMode}
            isMobile={isMobile}
            // Props for handlers
            setActiveSection={setActiveSection}
            setShowCreateTemplate={setShowCreateTemplate}
            handleCapture={handleCapture}
            handleFileUpload={handleFileUpload}
            handleSubmit={submitImages}
            handleRetake={discardImage}
            handleRemoveImage={removeImage}
            handleTemplateChange={handleEditTemplate}
            handleCompanyChange={handleEditTemplate}
            handleWithInventoryLedgerChange={handleWithInventoryLedgerChange}
            handleWithoutInventoryLedgerChange={
              handleWithoutInventoryLedgerChange
            }
            handleBatchProcessChange={handleBatchProcessChange}
            handleSplitPdfChange={handleSplitPdfChange}
            // Props for features
            hasFeature={hasFeature}
            fetchPresignedUrls={fetchPresignedUrls}
          />
        )}

        {/* Company Management Section */}
        {activeSection === "companies" && (
          <CompanyPage
            isDarkMode={isDarkMode}
            themeClasses={themeClasses}
            companies={companies}
            setCompanies={setCompanies}
            selectedCompany={selectedCompany}
            setSelectedCompany={setSelectedCompany}
            hasFeature={hasFeature}
            fetchPresignedUrls={fetchPresignedUrls}
          />
        )}

        {/* History Section */}
        {activeSection === "history" && (
          <HistorySection
            isDarkMode={isDarkMode}
            themeClasses={themeClasses}
            filteredDocuments={filteredDocuments}
            searchTerm={searchQuery}
            setSearchTerm={setSearchQuery}
            selectedCompany={selectedCompany}
            setSelectedCompany={setSelectedCompany}
            companies={companies}
            expandedDocs={expandedDocs}
            setExpandedDocs={setExpandedDocs}
            documentViewModes={documentViewModes}
            resultViewMode={resultViewMode}
            editingDocuments={editingDocuments}
            savingDocuments={savingDocuments}
            handleDocumentViewModeChange={handleDocumentViewModeChange}
            handleEditDocument={handleEditDocument}
            handleSaveDocument={handleSaveDocument}
            handleCancelEdit={handleCancelEdit}
            handleDeleteDocument={handleDeleteDocument}
            handleManualRefresh={handleManualRefresh}
            copyToClipboard={copyToClipboard}
            downloadAsCSV={downloadAsCSV}
            toggleResultViewMode={toggleResultViewMode}
            MediaViewer={MediaViewer}
            hasMediaToggle={true}
            presignedUrls={presignedUrls}
            loadingUrls={loadingUrls}
            fetchPresignedUrls={fetchPresignedUrls}
            fetchDocumentHistory={fetchDocumentHistory}
            historyStartDate={historyStartDate}
            historyEndDate={historyEndDate}
            historyStatusFilter={historyStatusFilter}
            historyStatusCounts={historyStatusCounts}
            onHistoryDateFilterChange={handleHistoryDateFilterChange}
            onHistoryStatusFilterChange={handleHistoryStatusFilterChange}
            onExportHistoryToExcel={exportHistoryToExcel}
            isExportingHistoryExcel={isExportingHistoryExcel}
            formatDateTime={formatDateTime}
            hasFeature={hasFeature}
            isLoadingHistory={isLoadingHistory}
            documentHistory={documentHistory}
            totalDocuments={totalDocuments}
            paginationInfo={paginationInfo}
            currentPage={currentPage}
            reprocessingDocuments={reprocessingDocuments}
          />
        )}

        {/* Review Section */}
        {activeSection === "review" && (
          <ReviewSection
            isDarkMode={isDarkMode}
            themeClasses={themeClasses}
            showReviewedDocs={showReviewedDocs}
            setShowReviewedDocs={setShowReviewedDocs}
            fetchReviewDocuments={fetchReviewDocuments}
            isLoadingReview={isLoadingReview}
            reviewDocuments={reviewDocuments}
            expandedReviewDocs={expandedReviewDocs}
            setExpandedReviewDocs={setExpandedReviewDocs}
            markingAsVerified={markingAsVerified}
            markDocumentAsVerified={markDocumentAsVerified}
            documentViewModes={documentViewModes}
            resultViewMode={resultViewMode}
            editingDocuments={editingDocuments}
            savingDocuments={savingDocuments}
            handleDocumentViewModeChange={handleDocumentViewModeChange}
            handleEditDocument={handleEditDocument}
            handleSaveDocument={handleSaveDocument}
            handleCancelEdit={handleCancelEdit}
            copyToClipboard={copyToClipboard}
            downloadAsCSV={downloadAsCSV}
            toggleResultViewMode={toggleResultViewMode}
            MediaViewer={MediaViewer}
            presignedUrls={presignedUrls}
            loadingUrls={loadingUrls}
            fetchPresignedUrls={fetchPresignedUrls}
            formatDateTime={formatDateTime}
            favicon={favicon}
          />
        )}

        {/* Status Section */}
        {activeSection === "status" && (
          <StatusSection
            isDarkMode={isDarkMode}
            themeClasses={themeClasses}
            statuses={statuses}
            expandedTasks={expandedTasks}
            templateNames={templateNames}
            resultViewMode={resultViewMode}
            editingDocuments={editingDocuments}
            toggleExpand={toggleExpand}
            formatDate={formatDate}
            getStatusColor={getStatusColor}
            copyToClipboard={copyToClipboard}
            downloadAsCSV={downloadAsCSV}
            toggleResultViewMode={toggleResultViewMode}
            handleEditDocument={handleEditDocument}
            handleCancelEdit={handleCancelEdit}
          />
        )}

        {/* Enhanced Templates Section */}
        {activeSection === "templates" && (
          <TemplateSection
            isDarkMode={isDarkMode}
            themeClasses={themeClasses}
            setShowCreateTemplate={setShowCreateTemplate}
            setShowEditTemplate={setShowEditTemplate}
            setSelectedTemplate={setSelectedTemplate}
          />
        )}

        {/* Usage Section */}
        {activeSection === "usage" && (
          <UsageSection
            isDarkMode={isDarkMode}
            themeClasses={themeClasses}
            setActiveSection={setActiveSection}
          />
        )}

        {/* Billing Section */}
        {activeSection === "billing" && (
          <BillingSection
            isDarkMode={isDarkMode}
            themeClasses={themeClasses}
            currentSubscription={currentSubscription}
            billingCycle={billingCycle}
            setBillingCycle={setBillingCycle}
            paymentLoading={paymentLoading}
            setPaymentLoading={setPaymentLoading}
            Card={Card}
          />
        )}

        {activeSection === "support" && (
          <SupportPage
            activeSection={activeSection}
            setActiveSection={setActiveSection}
            isDarkMode={isDarkMode}
            setIsDarkMode={setIsDarkMode}
          />
        )}

        {/* Integrations Section */}
        {activeSection === "integrations" &&
          (hasIntegrationsFeature() ? (
            <IntegrationsPage
              setActiveSection={setActiveSection}
              isDarkMode={isDarkMode}
              themeClasses={themeClasses}
              embedded={true}
            />
          ) : (
            <div className="flex items-center justify-center min-h-[400px] p-6">
              <div className="max-w-4xl w-full">
                {/* Main Card */}
                <div className="text-center">
                  {/* Lock Icon with animated background */}
                  <div className="relative mb-8">
                    <div
                      className={`w-20 h-20 mx-auto rounded-2xl flex items-center justify-center group-hover:scale-105 transition-transform duration-300 ${
                        isDarkMode
                          ? "bg-gradient-to-br from-gray-700 to-gray-800"
                          : "bg-gradient-to-br from-gray-100 to-gray-200"
                      }`}
                    >
                      <Lock
                        className={`w-10 h-10 ${
                          isDarkMode ? "text-gray-400" : "text-gray-600"
                        }`}
                      />
                    </div>
                    {/* Subtle pulse animation */}
                    <div
                      className={`absolute inset-0 w-20 h-20 mx-auto rounded-2xl animate-pulse opacity-20 ${
                        isDarkMode ? "bg-gray-600" : "bg-gray-300"
                      }`}
                    ></div>
                  </div>

                  {/* Main Content */}
                  <div className="space-y-6">
                    <div>
                      <h2
                        className={`text-3xl font-bold ${
                          isDarkMode ? "text-white" : "text-gray-800"
                        } mb-3 tracking-tight`}
                      >
                        Subscribe to Unlock Integrations
                      </h2>
                      {/* subscribe button */}
                      <div className="pt-4">
                        <button
                          onClick={() => setActiveSection("billing")}
                          className={`px-8 py-3 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 ${
                            isDarkMode
                              ? "bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-600/25"
                              : "bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-600/25"
                          }`}
                        >
                          Upgrade Now
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}

        {/* Create Template Modal */}
        {showCreateTemplate && (
          <CreateTemplate
            onClose={() => setShowCreateTemplate(false)}
            isDarkMode={isDarkMode}
            themeClasses={themeClasses}
            onTemplateCreated={handleTemplateCreated}
          />
        )}

        {/* Edit Template Modal */}
        {showEditTemplate && selectedTemplate && (
          <EditTemplate
            template={selectedTemplate}
            onClose={() => setShowEditTemplate(false)}
            isDarkMode={isDarkMode}
            themeClasses={themeClasses}
            onTemplateUpdated={handleTemplateUpdated}
          />
        )}

        {/* Sheet Selector Modal */}
        {_showSheetSelector && <SheetSelectorModal />}

        {/* Multiple Files Dialog */}
        {_showMultipleFilesDialog && (
          <div className="fixed inset-0 flex items-center justify-center z-50">
            <div
              className="absolute inset-0 bg-black bg-opacity-50"
              onClick={_handleMultipleFilesCancel}
            />
            <div
              className={`relative rounded-lg p-6 shadow-xl max-w-md w-full mx-4 ${
                isDarkMode
                  ? "bg-gray-800 border border-gray-700"
                  : "bg-white border border-gray-200"
              }`}
            >
              <h3 className={`text-xl font-semibold mb-4 ${themeClasses.text}`}>
                Multiple Files Selected
              </h3>
              <p className={`text-sm mb-6 ${themeClasses.subtext}`}>
                You've selected {pendingFiles.length} files. How would you like
                to process them?
              </p>

              <div className="space-y-3 mb-6">
                <div className="text-xs text-gray-500">
                  Files: {pendingFiles.map((f) => f.name).join(", ")}
                </div>
              </div>

              <div className="space-y-3">
                <button
                  onClick={() => _handleMultipleFilesConfirm(false)}
                  className={`w-full p-3 text-left rounded-lg transition-colors ${
                    isDarkMode
                      ? "hover:bg-gray-700 bg-gray-800 border border-gray-700"
                      : "hover:bg-gray-100 bg-white border border-gray-200"
                  } ${themeClasses.text}`}
                >
                  <div className="font-semibold">
                    Process as Separate Documents
                  </div>
                  <div className="text-sm text-gray-500">
                    Each file will be processed individually as a separate
                    document
                  </div>
                </button>

                <button
                  onClick={() => _handleMultipleFilesConfirm(true)}
                  className={`w-full p-3 text-left rounded-lg transition-colors ${
                    isDarkMode
                      ? "hover:bg-gray-700 bg-gray-800 border border-gray-700"
                      : "hover:bg-gray-100 bg-white border border-gray-200"
                  } ${themeClasses.text}`}
                >
                  <div className="font-semibold">
                    Process as Single Document
                  </div>
                  <div className="text-sm text-gray-500">
                    All files will be combined and processed as one document
                  </div>
                </button>
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={_handleMultipleFilesCancel}
                  className={`px-4 py-2 rounded-lg transition-colors ${
                    isDarkMode
                      ? "text-gray-400 hover:text-gray-200"
                      : "text-gray-600 hover:text-gray-800"
                  }`}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </Card>
    </DashboardLayout>
  );
}
